// Copyright (c) Bentley Systems
import { useCallback, useState } from "react";
import { RecordItem } from "./ViewModels/record-vm";
import {
  Anchor,
  Checkbox,
  ComboBox,
  Fieldset,
  SelectOption,
} from "@itwin/itwinui-react";
import { Field } from "./ViewModels/field-vm";
import { FieldType } from "./ViewModels/field-type";
import { MatchCode } from "./ViewModels/match-code";
import { MatchCodeItem } from "./ViewModels/match-code-item";
import { EfieldOptions } from "./ViewModels/efield-options";
import { EfColorPicker } from "./EfColorPicker";
import { useAppDispatch } from "../app/hooks";
import { setInvokeChange } from "../app/form-changed-slice";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import TextAreaComponent from "../CustomInputComponents/TextInputComponents/TextAreaComponent";

type RecordProps = {
  recordId: number;
  eformId: number;
  recordItems: RecordItem[];
  showAddButton: boolean;
  recordAddedFunction?: (newRecordItem: RecordItem) => void;
  recordRemovedFunction?: (recordItemToRemove: RecordItem) => void;
  callActionFunction?: (actionId: number) => void;
  allMatchCodes: MatchCode[];
  efieldOptions: EfieldOptions[];
};

const Record = (recordProps: RecordProps) => {
  const [recordItems, setRecordItems] = useState(recordProps.recordItems);
  const dispatch = useAppDispatch();

  function setEfieldChange(efField: Field, currentValue: any) {
    efField.selectedValue = currentValue;
    dispatch(setInvokeChange());
  }

  function renderSwitchFieldType(
    efField: Field,
    recordItemIndex: number,
    fieldIndex: number,
  ) {
    const componentKey = `field${efField.fieldId}_${recordItemIndex}_${fieldIndex}`;
    const labeledInput =
      efField.height === undefined ? (
        <LabeledInputComponent
          required={efField.required}
          validationType={efField.validationType}
          displayStyle="default"
          label={efField.label}
          placeholder="Enter text here..."
          disabled={!efField.editable}
          maxLength={efField.length}
          key={componentKey}
          defaultValue={efField.selectedValue}
          onChange={(currentValue) => {
            setEfieldChange(efField, currentValue.target.value);
          }}
          onBlur={(currentValue) => {
            efField.selectedValue = currentValue.target.value;
            if (
              efField.selectedValue !== "" &&
              efField.autoStartActionId &&
              recordProps.callActionFunction
            ) {
              recordProps.callActionFunction(efField.autoStartActionId);
            }
          }}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              efField.selectedValue !== "" &&
              efField.autoStartActionId &&
              recordProps.callActionFunction
            ) {
              recordProps.callActionFunction(efField.autoStartActionId);
            }
          }}
        />
      ) : (
        <div key={componentKey}>
          <div className="becs-input-grid">
            <div
              className={
                efField.required
                  ? "becs-input-label required"
                  : "becs-input-label"
              }
            >
              {efField.label}
            </div>
          </div>
          <TextAreaComponent
            required={efField.required}
            validationType={efField.validationType}
            placeholder="Enter text here..."
            style={{ height: efField.height }}
            disabled={!efField.editable}
            maxLength={efField.length}
            defaultValue={efField.selectedValue}
            onChange={(currentValue) => {
              setEfieldChange(efField, currentValue.target.value);
            }}
            onBlur={(currentValue) => {
              efField.selectedValue = currentValue.target.value;
              if (
                efField.selectedValue !== "" &&
                efField.autoStartActionId &&
                recordProps.callActionFunction
              ) {
                recordProps.callActionFunction(efField.autoStartActionId);
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                efField.selectedValue !== "" &&
                efField.autoStartActionId &&
                recordProps.callActionFunction
              ) {
                recordProps.callActionFunction(efField.autoStartActionId);
              }
            }}
          />
        </div>
      );
    switch (efField.type) {
      case FieldType.colorPicker:
        return (
          <EfColorPicker
            required={efField.required}
            label={efField.label}
            selectedValue={efField.selectedValue}
            key={componentKey}
            onChange={(currentValue) => {
              setEfieldChange(efField, currentValue);
            }}
          />
        );
      case FieldType.checkBox:
        return (
          <Checkbox
            required={efField.required}
            style={{ marginTop: "0.2rem" }}
            label={efField.label}
            key={componentKey}
            onChange={(e) => {
              setEfieldChange(efField, e.target.checked);
              if (efField.autoStartActionId && recordProps.callActionFunction) {
                recordProps.callActionFunction(efField.autoStartActionId);
              }
            }}
            defaultChecked={efField.selectedValue}
          />
        );
      case FieldType.dropDown:
        return efField.editable ? (
          <div key={componentKey}>
            <div className="becs-input-grid">
              <div
                className={
                  efField.required
                    ? "becs-input-label required"
                    : "becs-input-label"
                }
              >
                {efField.label}
              </div>
            </div>

            <ComboBox
              options={
                (recordProps.efieldOptions.find(
                  (o) => o.matchCodeId === efField.matchCodeId,
                )?.options as SelectOption<MatchCodeItem>[]) || []
              }
              onChange={(currentValue: MatchCodeItem) => {
                setEfieldChange(efField, currentValue.value);
                if (
                  efField.autoStartActionId &&
                  recordProps.callActionFunction
                ) {
                  recordProps.callActionFunction(efField.autoStartActionId);
                }
              }}
              value={getComboboxValue()}
            />
          </div>
        ) : (
          <LabeledInputComponent
            required={efField.required}
            validationType={efField.validationType}
            displayStyle="default"
            label={efField.label}
            disabled={true}
            maxLength={efField.length}
            key={componentKey}
            defaultValue={getComboboxValueForLabeledInput()}
          />
        );
      default:
        return labeledInput;
    }

    function getComboboxValueForLabeledInput(): string | undefined {
      const matchCodeItem = getComboboxValue();
      if (matchCodeItem !== undefined) {
        return matchCodeItem.text;
      }
    }

    function getComboboxValue(): MatchCodeItem | undefined {
      const efOptions = recordProps.efieldOptions.find(
        (o) => o.matchCodeId === efField.matchCodeId,
      );
      const options = efOptions?.options as SelectOption<MatchCodeItem>[];

      const savedValue = options?.find(
        (mci) => mci.value.value === efField.selectedValue,
      )?.value;
      if (savedValue !== undefined) {
        return savedValue;
      } else if (
        efOptions !== undefined &&
        efOptions.displayNull === false &&
        options !== undefined &&
        options.length > 0
      ) {
        return options[0].value;
      }
    }
  }

  const getNotHiddenFields = useCallback((recordItem: RecordItem): Field[] => {
    return recordItem.fields.filter((f) => f.hidden === false);
  }, []);

  function getFields(recordItem: RecordItem, recordItemIndex: number) {
    const notHiddenFields = getNotHiddenFields(recordItem);
    const fields = notHiddenFields.map((efField, index) => (
      <div key={`fields_${recordItemIndex}_${index}`}>
        {renderSwitchFieldType(efField, recordItemIndex, index)}
      </div>
    ));
    return fields;
  }

  const getDeleteAnchorStyle = useCallback(
    (recordItem: RecordItem): React.CSSProperties => {
      const notHiddenFields = getNotHiddenFields(recordItem);
      if (notHiddenFields.length > 1) {
        return {
          color: "var(--iui-color-text-negative)",
          display: "block",
          marginLeft: "auto",
          marginRight: 0,
          marginTop: "0.6rem",
        };
      } else {
        return {
          color: "var(--iui-color-text-negative)",
          display: "block",
          marginLeft: "auto",
          marginRight: 0,
          marginBottom: "-1.8rem",
          position: "relative",
          marginTop: "0.6rem",
        };
      }
    },
    [getNotHiddenFields],
  );

  function getDeleteIfNeeded(recordItem: RecordItem): React.JSX.Element | undefined {
    const showDelete = recordProps.showAddButton && recordItems.length > 1;
    if (showDelete) {
      return (
        <Anchor
          style={getDeleteAnchorStyle(recordItem)}
          as="button"
          onClick={() => {
            setRecordItems(recordItems.filter((item) => item !== recordItem));
            if (recordProps.recordRemovedFunction !== undefined) {
              recordProps.recordRemovedFunction(recordItem);
            }
            dispatch(setInvokeChange());
          }}
        >
          Remove
        </Anchor>
      );
    }
  }

  function cloneRecordItem(recordItem: RecordItem): RecordItem {
    const newRecordItem = new RecordItem();
    recordItem.fields.forEach((f) => {
      const fClone = JSON.parse(JSON.stringify(f)) as Field;
      fClone.selectedValue = undefined;
      newRecordItem.fields.push(fClone);
    });

    return newRecordItem;
  }

  const getFieldsetStyle = useCallback(
    (recordItem: RecordItem): React.CSSProperties => {
      const notHiddenFields = getNotHiddenFields(recordItem);
      if (recordProps.showAddButton && notHiddenFields.length > 1) {
        return {
          display: "flex",
          flexDirection: "column",
          background: "transparent",
          gap: "0.6rem",
        };
      } else {
        return {
          display: "flex",
          flexDirection: "column",
          background: "transparent",
          gap: "0.6rem",
          border: 0,
          padding: 0,
          marginTop: "0.6rem",
        };
      }
    },
    [getNotHiddenFields, recordProps.showAddButton],
  );

  return (
    <div>
      {recordItems.map((recordItem, index) => {
        const keyString =
          `fieldSet${  recordProps.recordId.toString()  }_${  index}`;
        return recordItem.fields.some((f) => f.hidden === false) ? (
          <div key={keyString}>
            <div>{getDeleteIfNeeded(recordItem)}</div>
            <Fieldset style={getFieldsetStyle(recordItem)}>
              {getFields(recordItem, index)}
            </Fieldset>
          </div>
        ) : (
          <div key={keyString}> </div>
        );
      })}
      {recordProps.showAddButton && (
        <Anchor
          className="rightAlign"
          as="button"
          onClick={() => {
            const newRecordItem = cloneRecordItem(recordProps.recordItems[0]);
            setRecordItems((current) => [...current, newRecordItem]);
            if (recordProps.recordAddedFunction !== undefined) {
              recordProps.recordAddedFunction(newRecordItem);
            }
          }}
        >
          Add
        </Anchor>
      )}
    </div>
  );
};

export default Record;
